import type { OverlayPaint } from '@mighty-map/map-engine';
import type { GeometryType, LegendPaint } from '../../api/types';
import type { LegendNode } from './legendModel';

/**
 * Layers the tools add to the map while the app runs, as Layers Pro lists them. In production these were real layers
 * on the map (the Coverage HUD's client-side analysis layers, Data Passport's layers from other themes and its file
 * and service imports), so the layer list showed them like any other: on top of the theme's layers, the last added
 * first, each with its own visibility box, a group add under the groups it came from, and in the legend with their
 * own symbols. Pure: the shapes and the ordering, tested in Node.
 */

export interface RuntimeLegendRow {
  label: string;
  geometry: GeometryType;
  paint: LegendPaint;
}

/** How a runtime layer is drawn, so Layers Pro can show and hide it: a published layer, or an engine overlay. */
export type RuntimeTarget = { kind: 'layer'; layerId: string } | { kind: 'overlay'; overlayId: string };

export interface RuntimeLayer {
  /** Stable while the layer is on the map. */
  id: string;
  title: string;
  /** The tool that put it there, for the reader. */
  source: string;
  /** Group titles above it, outermost first (a group add mirrors the groups it came from). */
  groupPath: readonly string[];
  legend: readonly RuntimeLegendRow[];
  target: RuntimeTarget;
  /** When it joined the map: the newest is listed first. */
  seq: number;
}

export interface RuntimeGroupNode {
  kind: 'group';
  /** Visibility key: `rtg:` and the group path. */
  key: string;
  title: string;
  children: RuntimeNode[];
}

export interface RuntimeLayerNode {
  kind: 'layer';
  layer: RuntimeLayer;
}

export type RuntimeNode = RuntimeGroupNode | RuntimeLayerNode;

/** The visibility key of the group at a path. */
export const groupKeyOf = (path: readonly string[]): string => `rtg:${path.join('\u001f')}`;

/**
 * The runtime layers as a tree, the way production's layer list showed them: the newest on top at the top level (a
 * layer added to the map draws and lists above what was there), a group where its first layer came, and inside a group
 * the order its layers were added in (a group add walks the catalog in order). Groups with the same title at the same
 * place merge, as production reused a group it had made.
 */
export function runtimeTree(layers: readonly RuntimeLayer[]): RuntimeNode[] {
  const root: RuntimeGroupNode = { kind: 'group', key: groupKeyOf([]), title: '', children: [] };
  for (const l of [...layers].sort((a, b) => a.seq - b.seq)) {
    let at = root;
    const path: string[] = [];
    for (const title of l.groupPath) {
      path.push(title);
      let g = at.children.find((c): c is RuntimeGroupNode => c.kind === 'group' && c.title === title);
      if (!g) {
        g = { kind: 'group', key: groupKeyOf(path), title, children: [] };
        at.children.push(g);
      }
      at = g;
    }
    at.children.push({ kind: 'layer', layer: l });
  }
  return [...root.children].reverse();
}

/** Whether a runtime layer is drawn: switched on, and every group above it too. */
export function runtimeShown(layer: RuntimeLayer, hidden: Readonly<Record<string, boolean>>): boolean {
  if (hidden[layer.id]) return false;
  for (let i = 1; i <= layer.groupPath.length; i++) if (hidden[groupKeyOf(layer.groupPath.slice(0, i))]) return false;
  return true;
}

/** The engine overlays and the published layers that are switched off in Layers Pro. */
export function hiddenTargets(
  layers: readonly RuntimeLayer[],
  hidden: Readonly<Record<string, boolean>>,
): { overlays: Set<string>; layers: Set<string> } {
  const out = { overlays: new Set<string>(), layers: new Set<string>() };
  for (const l of layers) {
    if (runtimeShown(l, hidden)) continue;
    if (l.target.kind === 'overlay') out.overlays.add(l.target.overlayId);
    else out.layers.add(l.target.layerId);
  }
  return out;
}

/** An overlay feature's look as a legend symbol (a point's marker outline is its outline). */
export function legendPaintOf(p: OverlayPaint, geometry: GeometryType): LegendPaint {
  const point = geometry.endsWith('Point');
  const stroke = point ? (p.markerStroke ?? p.stroke) : p.stroke;
  const strokeWidth = point ? (p.markerStrokeWidth ?? p.strokeWidth) : p.strokeWidth;
  return {
    ...(p.fill !== undefined ? { fill: p.fill } : {}),
    ...(p.fillOpacity !== undefined ? { fillOpacity: p.fillOpacity } : {}),
    ...(stroke !== undefined && strokeWidth !== 0 ? { stroke } : {}),
    ...(strokeWidth !== undefined && strokeWidth !== 0 ? { strokeWidth } : {}),
    ...(p.radius !== undefined ? { radius: p.radius } : {}),
    ...(p.dashed ? { dash: [4, 2] } : {}),
  };
}

/** A row of the runtime block at the top of the Layers tab. */
export type RuntimeRow =
  | { kind: 'group'; path: string; key: string; title: string; depth: number; open: boolean; layerIds: string[] }
  | { kind: 'layer'; path: string; key: string; layer: RuntimeLayer; depth: number };

const norm = (s: string): string => s.toLocaleLowerCase().trim();

/** Published layer ids under a node (Zoom to and the labels toggle work on those). */
function publishedUnder(n: RuntimeNode): string[] {
  if (n.kind === 'layer') return n.layer.target.kind === 'layer' ? [n.layer.target.layerId] : [];
  return n.children.flatMap(publishedUnder);
}

/**
 * The runtime tree as rows, as the theme's rows are listed: only inside open groups (a group starts shut, as a group
 * the config does not open does), and while searching only what matches, with its groups.
 */
export function runtimeRows(
  tree: readonly RuntimeNode[],
  opts: { open: Readonly<Record<string, boolean>>; query: string },
): RuntimeRow[] {
  const q = norm(opts.query);
  const searching = q.length > 0;
  const titleOf = (n: RuntimeNode): string => (n.kind === 'group' ? n.title : n.layer.title);
  const hasMatch = (n: RuntimeNode): boolean =>
    norm(titleOf(n)).includes(q) || (n.kind === 'group' && n.children.some(hasMatch));
  const out: RuntimeRow[] = [];
  const walk = (nodes: readonly RuntimeNode[], depth: number, parent: string, showAll: boolean): void => {
    for (const n of nodes) {
      if (searching && !showAll && !hasMatch(n)) continue;
      if (n.kind === 'group') {
        const path = `${parent}/${n.key}`;
        const open = searching ? true : (opts.open[n.key] ?? false);
        out.push({ kind: 'group', path, key: n.key, title: n.title, depth, open, layerIds: publishedUnder(n) });
        if (open) walk(n.children, depth + 1, path, showAll || (searching && norm(n.title).includes(q)));
      } else {
        out.push({ kind: 'layer', path: `${parent}/${n.layer.id}`, key: n.layer.id, layer: n.layer, depth });
      }
    }
  };
  walk(tree, 0, 'rt', false);
  return out;
}

/**
 * The runtime layers in the Legend tab, above the theme's (production's legend listed every layer on the map, the
 * newest on top): the layers `shown` keeps, under their groups. A layer from another theme keeps its layer id, so
 * "Visible features only" can ask about it; a layer a tool draws has none, and is kept.
 */
export function runtimeLegend(tree: readonly RuntimeNode[], shown: (layer: RuntimeLayer) => boolean): LegendNode[] {
  const walk = (n: RuntimeNode): LegendNode | null => {
    if (n.kind === 'group') {
      const children = n.children.map(walk).filter((c): c is LegendNode => c !== null);
      return children.length ? { isGroup: true, key: n.key, title: n.title, children } : null;
    }
    const l = n.layer;
    if (!shown(l) || !l.legend.length) return null;
    return {
      isGroup: false,
      key: `rt:${l.id}|${l.title}`,
      title: l.title,
      rows: l.legend.map((r) => ({ label: r.label, symbol: { geometry: r.geometry, paint: r.paint } })),
      ...(l.target.kind === 'layer' ? { layerId: l.target.layerId } : {}),
    };
  };
  return tree.map(walk).filter((c): c is LegendNode => c !== null);
}
